import { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import { useSnackbar } from "notistack";
import { useScriptActions, useScriptDetail } from "../../hooks/useScripts";
import { formatCommentTimestamp, initialsFor, linkifyText } from "../brief/briefUiUtils";
import {
  ACTOR_COLORS,
  ApprovalPills,
  CreatorCell,
  PARTY_LABELS,
  StatusChip,
  errorMessage,
  primaryButtonSx,
  secondaryButtonSx,
} from "./scriptUi";

// The one script view every audience uses — the team's drawer on the board,
// and the brand's / creator's public share-link page. `viewer` is "team",
// "brand" or "creator"; what each can do follows the backend's rules
// (helpers/scriptWorkflow.js): anyone can edit (each save = a new version) and
// remark; only the brand approves or requests changes (the team can record
// that on the brand's behalf); once the brand approves, the share links go
// read-only until the team saves a new version.

const LinkifiedText = ({ text }) =>
  linkifyText(text || "").map((piece, i) =>
    piece.url ? (
      <a key={i} href={piece.url} target="_blank" rel="noopener noreferrer" style={{ color: "#FF5200", wordBreak: "break-all" }}>
        {piece.url}
      </a>
    ) : (
      <span key={i}>{piece.text}</span>
    )
  );

const ACTIVITY_TEXT = {
  version: (a) => `saved version ${a.version}`,
  approved: (a) => (a.onBehalfOf ? `marked v${a.version} approved for the ${PARTY_LABELS[a.onBehalfOf].toLowerCase()}` : `approved v${a.version}`),
  changes_requested: (a) =>
    a.onBehalfOf ? `recorded changes requested by the ${PARTY_LABELS[a.onBehalfOf].toLowerCase()} on v${a.version}` : `requested changes on v${a.version}`,
  remark: () => null,
};

const ActivityItem = ({ item }) => {
  const action = ACTIVITY_TEXT[item.kind](item);
  return (
    <Box sx={{ display: "flex", gap: 1.25 }}>
      <Avatar sx={{ width: 30, height: 30, fontSize: 12, fontWeight: 700, bgcolor: ACTOR_COLORS[item.author.type], flexShrink: 0 }}>
        {initialsFor(item.author.name.replace(/^@/, ""))}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{item.author.name}</Typography>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: ACTOR_COLORS[item.author.type] }}>
            {PARTY_LABELS[item.author.type]}
          </Typography>
          {action && <Typography sx={{ fontSize: 13, color: "#6B7280" }}>{action}</Typography>}
          <Typography sx={{ fontSize: 11.5, color: "#9CA3AF" }}>{formatCommentTimestamp(item.createdOn)}</Typography>
        </Box>
        {item.body && (
          <Typography
            sx={{
              fontSize: 13.5,
              color: "#374151",
              mt: 0.25,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              ...(item.kind === "changes_requested" && { borderLeft: "3px solid #F59E0B", pl: 1 }),
            }}
          >
            <LinkifiedText text={item.body} />
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// Shared by "Request changes" (brand) and "Record decision" (team, always on
// the brand's behalf). The note is required for changes — the backend rejects
// a change request without one.
const DecisionDialog = ({ open, onClose, viewer, version, isSaving, onSubmit }) => {
  const isTeam = viewer === "team";
  const [decision, setDecision] = useState("changes_requested");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (open) {
      setDecision("changes_requested");
      setNote("");
    }
  }, [open]);

  const needsNote = decision === "changes_requested" && !note.trim();
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: "16px" } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>{isTeam ? `Record a decision on v${version}` : `Request changes on v${version}`}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
        {isTeam && (
          <ToggleButtonGroup exclusive size="small" value={decision} onChange={(_, v) => v && setDecision(v)}>
            <ToggleButton value="approved" sx={{ textTransform: "none", px: 2 }}>Approved</ToggleButton>
            <ToggleButton value="changes_requested" sx={{ textTransform: "none", px: 2 }}>Changes requested</ToggleButton>
          </ToggleButtonGroup>
        )}
        <TextField
          multiline
          minRows={4}
          autoFocus
          value={note}
          onChange={(e) => setNote(e.target.value)}
          inputProps={{ maxLength: 2000 }}
          placeholder={decision === "changes_requested" ? "What should change? (required)" : "Add a note (optional)"}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button
          variant="contained"
          disabled={needsNote || isSaving}
          onClick={() => onSubmit({ ...(isTeam && { party: "brand" }), decision, version, note: note.trim() })}
          sx={primaryButtonSx}
        >
          {isSaving ? <CircularProgress size={18} color="inherit" /> : isTeam ? "Save decision" : "Send to team"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ScriptReviewPanel = ({ scope, scriptId, viewer }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { data: script, isLoading, error } = useScriptDetail(scope, scriptId);
  const { saveVersion, decide, addRemark } = useScriptActions(scope, scriptId);

  // Edits always start from the latest version and remember which one —
  // the backend 409s the save if someone else saved in between.
  const [draft, setDraft] = useState(null);
  const [viewedVersion, setViewedVersion] = useState(null);
  const [remark, setRemark] = useState("");
  const [decisionOpen, setDecisionOpen] = useState(false);

  useEffect(() => {
    setDraft(null);
    setViewedVersion(null);
    setRemark("");
  }, [scriptId]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: "#FF5200" }} />
      </Box>
    );
  }
  if (error || !script) {
    return <Typography sx={{ p: 3, color: "#B91C1C" }}>{errorMessage(error, "Couldn't load this script.")}</Typography>;
  }

  const isTeam = viewer === "team";
  const lockedForParty = !isTeam && script.status === "approved";
  const current = script.currentVersion;
  const shown = script.versions.find((v) => v.number === (viewedVersion ?? current));
  const myDecision = !isTeam ? script.approvals[viewer]?.decision : null;

  const run = (mutation, payload, success, after) =>
    mutation.mutate(payload, {
      onSuccess: () => {
        if (success) enqueueSnackbar(success, { variant: "success" });
        after?.();
      },
      onError: (e) => enqueueSnackbar(errorMessage(e), { variant: "error" }),
    });

  const handleSave = () =>
    run(saveVersion, { body: draft.body, baseVersion: draft.baseVersion }, `Saved as version ${current + 1}`, () => {
      setDraft(null);
      setViewedVersion(null);
    });

  const handleDecision = (payload) =>
    run(decide, payload, payload.decision === "approved" ? "Approved" : "Change request sent", () => setDecisionOpen(false));

  const handleRemark = () => run(addRemark, { body: remark.trim() }, null, () => setRemark(""));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {/* Who + where it stands */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <CreatorCell script={script} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <ApprovalPills approvals={script.approvals} />
          <StatusChip status={script.status} />
        </Box>
      </Box>

      {/* Script */}
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, gap: 1, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 15 }}>Script</Typography>
            {current > 0 && !draft && (
              <Select
                size="small"
                value={viewedVersion ?? current}
                onChange={(e) => setViewedVersion(e.target.value === current ? null : e.target.value)}
                sx={{ fontSize: 13, height: 30, borderRadius: "8px" }}
              >
                {script.versions.map((v) => (
                  <MenuItem key={v.number} value={v.number} sx={{ fontSize: 13 }}>
                    v{v.number}{v.number === current ? " (latest)" : ""} · {v.author.name}
                  </MenuItem>
                ))}
              </Select>
            )}
          </Box>
          {!draft && !lockedForParty && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditOutlinedIcon fontSize="small" />}
              onClick={() => setDraft({ body: script.body, baseVersion: current })}
              sx={secondaryButtonSx}
            >
              {current === 0 ? "Write script" : "Edit"}
            </Button>
          )}
        </Box>

        {draft ? (
          <Box>
            <TextField
              fullWidth
              multiline
              minRows={10}
              autoFocus
              value={draft.body}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
              inputProps={{ maxLength: 20000 }}
              placeholder="Write the script here…"
              sx={{ "& .MuiInputBase-root": { bgcolor: "#FAFAFA", borderRadius: "12px", fontSize: 14, lineHeight: 1.6 } }}
            />
            <Typography sx={{ fontSize: 12, color: "#6B7280", mt: 0.75 }}>
              Saving creates version {draft.baseVersion + 1}
              {draft.baseVersion > 0 && " and asks the brand to approve again"}.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 1 }}>
              <Button onClick={() => setDraft(null)} sx={{ textTransform: "none" }}>Cancel</Button>
              <Button
                variant="contained"
                disabled={!draft.body.trim() || draft.body === script.body || saveVersion.isLoading}
                onClick={handleSave}
                sx={primaryButtonSx}
              >
                {saveVersion.isLoading ? <CircularProgress size={18} color="inherit" /> : "Save new version"}
              </Button>
            </Box>
          </Box>
        ) : shown ? (
          <Box sx={{ border: "1px solid #EEF0F3", bgcolor: "#FAFAFA", borderRadius: "12px", p: 2 }}>
            <Typography sx={{ fontSize: 12, color: "#6B7280", mb: 1 }}>
              v{shown.number} by {shown.author.name} ({PARTY_LABELS[shown.author.type]}) · {formatCommentTimestamp(shown.createdOn)}
              {shown.number !== current && " · older version"}
            </Typography>
            <Typography sx={{ fontSize: 14, lineHeight: 1.65, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              <LinkifiedText text={shown.body} />
            </Typography>
          </Box>
        ) : (
          <Box sx={{ border: "1px dashed #E5E7EB", borderRadius: "12px", p: 3, textAlign: "center" }}>
            <Typography sx={{ fontSize: 13.5, color: "#6B7280" }}>No script yet.</Typography>
          </Box>
        )}
      </Box>

      {/* Decisions — the creator doesn't sign off, so their link only sees the final state. */}
      {current > 0 && !draft && (viewer !== "creator" || lockedForParty) && (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          {lockedForParty ? (
            <Typography sx={{ fontSize: 13, color: "#065F46", fontWeight: 600 }}>
              Approved by the brand. Ask the team if anything needs to change.
            </Typography>
          ) : isTeam ? (
            <Button variant="outlined" onClick={() => setDecisionOpen(true)} sx={secondaryButtonSx}>
              Record brand decision
            </Button>
          ) : (
            <>
              <Button
                variant="contained"
                startIcon={<CheckRoundedIcon />}
                disabled={myDecision === "approved" || decide.isLoading}
                onClick={() => handleDecision({ decision: "approved", version: current })}
                sx={primaryButtonSx}
              >
                {myDecision === "approved" ? `You approved v${current}` : `Approve v${current}`}
              </Button>
              <Button
                variant="outlined"
                startIcon={<ReplayRoundedIcon />}
                onClick={() => setDecisionOpen(true)}
                sx={secondaryButtonSx}
              >
                Request changes
              </Button>
            </>
          )}
        </Box>
      )}

      <Divider />

      {/* One shared thread — team, brand and creator all see every entry. */}
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 1.5 }}>Remarks & activity</Typography>
        {script.activity.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: "#9CA3AF", fontStyle: "italic", mb: 1.5 }}>Nothing yet.</Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75, mb: 2 }}>
            {script.activity.map((item) => (
              <ActivityItem key={item.id} item={item} />
            ))}
          </Box>
        )}
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
          <TextField
            fullWidth
            multiline
            maxRows={6}
            size="small"
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            inputProps={{ maxLength: 2000 }}
            placeholder="Add a remark — everyone on this script will see it"
            sx={{ "& .MuiInputBase-root": { borderRadius: "10px", fontSize: 14 } }}
          />
          <Button
            variant="contained"
            disabled={!remark.trim() || addRemark.isLoading}
            onClick={handleRemark}
            sx={{ ...primaryButtonSx, px: 2, minWidth: 0, height: 40 }}
          >
            {addRemark.isLoading ? <CircularProgress size={18} color="inherit" /> : <SendRoundedIcon fontSize="small" />}
          </Button>
        </Box>
      </Box>

      <DecisionDialog
        open={decisionOpen}
        onClose={() => setDecisionOpen(false)}
        viewer={viewer}
        version={current}
        isSaving={decide.isLoading}
        onSubmit={handleDecision}
      />
    </Box>
  );
};

export default ScriptReviewPanel;
